import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { v4 as uuidv4 } from 'uuid';

import getExternalTooltipDate from './utils/getExternalTooltipDate';
import getExternalTooltipHtml from './utils/getExternalTooltipHtml';
import getExternalTooltipPercentageFormattedValue from './utils/getExternalTooltipPercentageFormattedValue';
import setExternalTooltip from './utils/setExternalTooltip';
import NoData from './NoData/NoData';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const colors = [
  '#0F273C',
  '#EEBF00',
  '#05ACC1',
  '#5FBFF9',
  '#D7263D',
  '#F26419',
  '#21C581',
  '#B7E4FF',
  '#6C7A89',
  '#FEAF31',
  '#4D4FB7',
  '#8C49A7',
  '#F25A4C',
  '#91CA5F',
  '#CF3F9D',
  '#3F41CF',
  '#377CA6',
  '#085C53',
  '#B28F00',
  '#730000',
  '#FEDD58',
  '#4E6E8A',
  '#500D6B',
  '#8B0B22',
  '#6C757D',
  '#657350',
  '#C4FFF9',
];

const MAX_VISIBLE_LEGEND = 8;

const tooltipId = uuidv4();

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  title: {
    fontSize: '16px',
    fontWeight: '500',
    padding: '6px 16px',
  },
  legendContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginBottom: '18px',
  },
  legendItems: {
    display: 'flex',
    justifyContent: 'center',
    columnGap: '16px',
    rowGap: '8px',
    flexWrap: 'wrap',
    margin: '8px 0',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
  },
  legendColorBox: {
    width: '14px',
    height: '14px',
    marginRight: '8px',
  },
  showAllButton: {
    background: 'none',
    border: 'none',
    color: '#9C2AB1',
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: '0',
    fontSize: '14px',
    fontWeight: '400',
  },
  chartWrapper: {
    position: 'relative',
    width: '297px',
    height: '297px',
  },
  chartContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: '1',
  },
  spinnerWrapper: {
    position: 'absolute',
    display: 'grid',
    placeItems: 'center',
    inset: '0',
  },
});

const DoughnutChart = ({ statistics, isLoading }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [isLegendExpanded, setIsLegendExpanded] = useState(false);

  if (isLoading) {
    return (
      <div className={classes.spinnerWrapper}>
        <CircularProgress />
      </div>
    );
  }

  const hasData = statistics.knowAboutUs?.dataSeries.length > 0;

  const sortedData = hasData
    ? [...statistics.knowAboutUs.dataSeries].sort((a, b) => b.value - a.value)
    : [];

  const chartData = {
    labels: sortedData.map(stat => stat.name),
    datasets: [
      {
        data: sortedData.map(stat => stat.value),
        backgroundColor: colors,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: { display: false },
      title: { display: false },
      tooltip: {
        enabled: false,
        external: ({ chart, tooltip }) => {
          if (!tooltip.dataPoints) {
            return;
          }

          const [{ dataIndex, element, label, raw }] = tooltip.dataPoints;
          const { dateFrom, dateTo } = sortedData[dataIndex];

          setExternalTooltip({
            tooltipId,
            chart,
            tooltip,
            tooltipInnerHTML: getExternalTooltipHtml({
              title: label,
              color: element.options.backgroundColor,
              content: [
                {
                  text: getExternalTooltipDate(dateFrom, dateTo),
                },
                {
                  highlightedText: getExternalTooltipPercentageFormattedValue({
                    raw,
                    total: statistics.knowAboutUs.total,
                  }),
                  text: t('dashboard.answers.count', {
                    count: raw,
                  }),
                },
              ],
            }),
          });
        },
      },
    },
  };

  const visibleItems = isLegendExpanded
    ? sortedData
    : sortedData.slice(0, MAX_VISIBLE_LEGEND);

  return (
    <div className={classes.root}>
      <p className={classes.title}>{t('dashboard.knowAboutUs.title')}</p>
      <div className={classes.legendContainer}>
        <div className={classes.legendItems}>
          {visibleItems.map((item, index) => (
            <div key={item.name} className={classes.legendItem}>
              <span
                className={classes.legendColorBox}
                style={{
                  background: colors[index % colors.length],
                }}
              />
              <span>{item.name}</span>
            </div>
          ))}
        </div>
        {sortedData.length > MAX_VISIBLE_LEGEND && (
          <button
            className={classes.showAllButton}
            type="button"
            onClick={() => {
              setIsLegendExpanded(prev => !prev);
            }}
          >
            {isLegendExpanded
              ? t('dashboard.knowAboutUs.legend.collapse')
              : t('dashboard.knowAboutUs.legend.showAll')}
          </button>
        )}
      </div>
      <div className={classes.chartContainer}>
        <div className={classes.chartWrapper}>
          <Doughnut data={chartData} options={options} />
          {!hasData && <NoData />}
        </div>
      </div>
    </div>
  );
};

export default DoughnutChart;
